import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import {
  Gauge, LogOut, Upload, Users, FileText, Trash2, Hash, MessageSquare, Package, FileSignature, Reply, Send, X, UserPlus, Edit2, Save, Search, Layers
} from "lucide-react";
import { toast } from "sonner";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import QuotesTab from "@/components/admin/QuotesTab";
import ProductsTab from "@/components/admin/ProductsTab";
import AdminSGMTab from "@/components/admin/AdminSGMTab";
import InvoicesTab from "@/components/admin/InvoicesTab";
import MessagesTab from "@/components/admin/MessagesTab";
import CommandPalette from "@/components/admin/CommandPalette";
import { ThemeToggle } from "@/components/site/ThemeToggle";
import { usePagination } from "@/lib/usePagination";

export default function AdminPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentMonth = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    user_id: "", description: "", amount: "", period: currentMonth(), invoice_number: "", file: null, group_id: "",
  });
  const [uploading, setUploading] = useState(false);
  const [clientGroups, setClientGroups] = useState([]);  // SGM groups of the selected user_id
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [activeTab, setActiveTab] = useState("invoices");
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyTarget, setReplyTarget] = useState(null);
  const [replyForm, setReplyForm] = useState({ subject: "", body: "" });
  const [sendingReply, setSendingReply] = useState(false);

  // New message notification
  const SEEN_KEY = "sgm_seen_msg_ids";
  const getSeenIds = () => {
    try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); } catch { return new Set(); }
  };
  const persistSeenIds = (set) => {
    try { localStorage.setItem(SEEN_KEY, JSON.stringify([...set])); } catch { /* noop */ }
  };
  const [seenIds, setSeenIds] = useState(() => getSeenIds());
  const [newMessageDialog, setNewMessageDialog] = useState(null);
  const unreadMessages = messages.filter((m) => !seenIds.has(m.id));
  const unreadCount = unreadMessages.length;

  // Pagination (client-side) for long tables — invoices & messages now live in their tabs.
  const clientsPager = usePagination(users, 10, "admin-clients-pager");

  // Global command palette (Cmd/Ctrl + K)
  const [paletteOpen, setPaletteOpen] = useState(false);
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const handlePaletteNavigate = (item) => {
    if (!item?.tab) return;
    setActiveTab(item.tab);
    // Smooth scroll to the matched item (if rendered) after the tab opens
    setTimeout(() => {
      const target = document.querySelector(`[data-testid$="-${item.id}"]`) ||
                     document.querySelector(`[data-testid$="${item.id}"]`);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        target.classList.add("ring-2", "ring-[#F59E0B]");
        setTimeout(() => target.classList.remove("ring-2", "ring-[#F59E0B]"), 1800);
      }
    }, 250);
  };

  const markAllSeen = () => {
    const next = new Set(seenIds);
    messages.forEach((m) => next.add(m.id));
    setSeenIds(next);
    persistSeenIds(next);
  };
  const markSeen = (id) => {
    const next = new Set(seenIds);
    next.add(id);
    setSeenIds(next);
    persistSeenIds(next);
  };

  // Client edit / create
  const emptyClient = { id: null, name: "", email: "", company: "", phone: "", password: "" };
  const [clientOpen, setClientOpen] = useState(false);
  const [clientForm, setClientForm] = useState(emptyClient);
  const [clientSaving, setClientSaving] = useState(false);

  const openCreateClient = () => { setClientForm(emptyClient); setClientOpen(true); };
  const openEditClient = (u) => {
    setClientForm({ id: u.id, name: u.name || "", email: u.email, company: u.company || "", phone: u.phone || "", password: "" });
    setClientOpen(true);
  };
  const saveClient = async (e) => {
    e.preventDefault();
    setClientSaving(true);
    try {
      if (clientForm.id) {
        const body = {
          name: clientForm.name,
          email: clientForm.email,
          company: clientForm.company,
          phone: clientForm.phone,
        };
        if (clientForm.password) body.password = clientForm.password;
        await api.put(`/admin/users/${clientForm.id}`, body);
        toast.success("Cliente actualizado");
      } else {
        if (!clientForm.password || clientForm.password.length < 6) {
          throw new Error("La contraseña debe tener al menos 6 caracteres");
        }
        await api.post("/admin/users", {
          name: clientForm.name,
          email: clientForm.email,
          company: clientForm.company,
          phone: clientForm.phone,
          password: clientForm.password,
        });
        toast.success("Cliente creado");
      }
      setClientOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Error");
    } finally {
      setClientSaving(false);
    }
  };
  const deleteClient = async (u) => {
    const ok = window.confirm(
      `¿Eliminar al cliente "${u.name}" (${u.email})?\n\nSe eliminarán también sus facturas asignadas. Las cotizaciones quedarán archivadas sin cliente asociado.`
    );
    if (!ok) return;
    try {
      const { data } = await api.delete(`/admin/users/${u.id}`);
      toast.success(`Cliente eliminado · ${data.deleted_invoices} factura(s) borradas`);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al eliminar");
    }
  };

  const openReply = (m) => {
    setReplyTarget(m);
    setReplyForm({
      subject: `Re: Su solicitud a SGM México${m.service ? ` (${m.service})` : ""}`,
      body: `Hola ${m.name || ""},\n\nGracias por su mensaje. `,
    });
    setReplyOpen(true);
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!replyTarget) return;
    setSendingReply(true);
    try {
      await api.post(`/admin/contact-messages/${replyTarget.id}/reply`, {
        subject: replyForm.subject,
        body: replyForm.body,
      });
      toast.success(`Respuesta enviada a ${replyTarget.email}`);
      setReplyOpen(false);
      setReplyTarget(null);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al enviar");
    } finally {
      setSendingReply(false);
    }
  };

  const fetchAll = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [u, i, m] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/invoices"),
        api.get("/admin/contact-messages"),
      ]);
      setUsers(u.data);
      setInvoices(i.data);
      // Detect new (unseen) messages and show dialog with the most recent one
      const currentSeen = getSeenIds();
      const incomingUnseen = (m.data || []).filter((msg) => !currentSeen.has(msg.id));
      const previousIds = new Set(messages.map((x) => x.id));
      const trulyNew = incomingUnseen.filter((msg) => !previousIds.has(msg.id));
      setMessages(m.data);
      if (silent && trulyNew.length > 0) {
        setNewMessageDialog(trulyNew[0]);
        toast.message(`Nuevo mensaje de ${trulyNew[0].name}`, {
          description: trulyNew[0].email,
          duration: 6000,
        });
      }
    } catch (e) {
      if (!silent) toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Poll for new messages every 30 seconds
  useEffect(() => {
    const id = setInterval(() => { fetchAll({ silent: true }); }, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // Auto mark visible messages as seen when admin opens the Mensajes tab
  useEffect(() => {
    if (activeTab === "messages" && unreadCount > 0) {
      const t = setTimeout(() => markAllSeen(), 600);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, unreadCount]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) return toast.error("Seleccione un PDF");
    if (!uploadForm.user_id) return toast.error("Seleccione un cliente");
    const fd = new FormData();
    fd.append("file", uploadForm.file);
    fd.append("user_id", uploadForm.user_id);
    fd.append("description", uploadForm.description || "Factura SGM");
    fd.append("amount", uploadForm.amount || "0");
    fd.append("period", uploadForm.period || "");
    fd.append("invoice_number", uploadForm.invoice_number || "");
    if (uploadForm.group_id) fd.append("group_id", uploadForm.group_id);
    setUploading(true);
    try {
      await api.post("/admin/invoices/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(uploadForm.group_id ? "Factura subida y asignada al grupo" : "Factura subida correctamente");
      setUploadOpen(false);
      setUploadForm({ user_id: "", description: "", amount: "", period: currentMonth(), invoice_number: "", file: null, group_id: "" });
      setClientGroups([]);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  // When the client changes inside the upload dialog, load their SGM groups for the optional Group assignment
  useEffect(() => {
    if (!uploadOpen || !uploadForm.user_id) { setClientGroups([]); return; }
    let cancelled = false;
    (async () => {
      setLoadingGroups(true);
      try {
        const { data } = await api.get("/admin/sgm-groups");
        if (cancelled) return;
        setClientGroups((data || []).filter((g) => g.user_id === uploadForm.user_id));
      } catch {
        if (!cancelled) setClientGroups([]);
      } finally {
        if (!cancelled) setLoadingGroups(false);
      }
    })();
    return () => { cancelled = true; };
  }, [uploadOpen, uploadForm.user_id]);

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta factura? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/admin/invoices/${id}`);
      toast.success("Factura eliminada");
      fetchAll();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const handleDownload = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", inv.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error("Error al descargar");
    }
  };

  const formatMoney = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);
  const formatDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1F] text-white" data-testid="admin-portal">
      <header className="border-b border-[#2E2E33] bg-[#1F1F25] sticky top-0 z-40 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-16 sm:h-20 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="admin-logo">
            <div className="w-9 h-9 sm:w-10 sm:h-10 border border-[#F59E0B] flex items-center justify-center bg-[#F59E0B]/10 flex-shrink-0">
              <Gauge className="w-5 h-5 text-[#F59E0B]" strokeWidth={2.5} />
            </div>
            <div className="leading-tight min-w-0">
              <div className="font-heading font-black text-sm uppercase truncate">SGM México</div>
              <div className="font-mono text-[10px] text-[#F59E0B] tracking-widest uppercase">Panel Admin</div>
            </div>
          </Link>
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <span className="hidden md:block font-mono text-xs text-[#A1A1AA] uppercase tracking-widest">// {user?.email}</span>
            <button
              onClick={() => setPaletteOpen(true)}
              className="hidden sm:inline-flex items-center gap-2 border border-[#2E2E33] hover:border-[#F59E0B] hover:text-[#F59E0B] px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA] transition-colors"
              data-testid="admin-search-trigger"
              title="Buscar (⌘K / Ctrl+K)"
            >
              <Search className="w-3.5 h-3.5" />
              <span>Buscar</span>
              <kbd className="font-mono text-[9px] border border-[#2E2E33] px-1 py-0.5">⌘K</kbd>
            </button>
            <button
              onClick={() => setPaletteOpen(true)}
              className="sm:hidden border border-[#2E2E33] hover:border-[#F59E0B] hover:text-[#F59E0B] p-2"
              data-testid="admin-search-trigger-mobile"
              aria-label="Buscar"
            >
              <Search className="w-4 h-4" />
            </button>
            <ThemeToggle testId="admin-theme-toggle" />
            <button onClick={async () => { await logout(); navigate("/"); }} className="btn-ghost px-3 py-2 sm:px-7 sm:py-4" data-testid="admin-logout">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 sm:gap-6 mb-8 sm:mb-12">
          <div>
            <div className="overline mb-3">// Control Center</div>
            <h1 className="font-heading font-black uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tighter">
              Panel de <span className="text-[#F59E0B]">Administración</span>
            </h1>
            <p className="text-[#A1A1AA] mt-3 text-sm sm:text-base">Gestione clientes, facturas y mensajes de contacto.</p>
          </div>
          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <button className="btn-primary w-full sm:w-auto" data-testid="open-upload-btn">
                <Upload className="w-4 h-4" /> Subir Factura
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#1F1F25] border-[#2E2E33] text-white max-w-lg rounded-none" data-testid="upload-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading font-black uppercase tracking-tight">Subir nueva factura</DialogTitle>
                <DialogDescription className="text-[#A1A1AA]">Asigne el PDF a un cliente registrado.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUpload} className="space-y-4">
                <div>
                  <label className="overline block mb-2">Cliente</label>
                  <Select value={uploadForm.user_id} onValueChange={(v) => setUploadForm({ ...uploadForm, user_id: v })}>
                    <SelectTrigger className="input-base rounded-none" data-testid="upload-client-select">
                      <SelectValue placeholder="Seleccione cliente" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1F1F25] border-[#2E2E33] text-white rounded-none">
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id} className="font-mono text-sm">
                          {u.email} {u.company ? `· ${u.company}` : ""}
                        </SelectItem>
                      ))}
                      {users.length === 0 && <SelectItem value="none" disabled>No hay clientes</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                {uploadForm.user_id && (
                  <div>
                    <label className="overline block mb-2 flex items-center gap-2">
                      <Layers className="w-3 h-3 text-[#F59E0B]" /> Grupo SGM Web (opcional)
                    </label>
                    <Select
                      value={uploadForm.group_id || "none"}
                      onValueChange={(v) => setUploadForm({ ...uploadForm, group_id: v === "none" ? "" : v })}
                    >
                      <SelectTrigger className="input-base h-12" data-testid="upload-group-select">
                        <SelectValue placeholder={loadingGroups ? "Cargando grupos..." : "Sin grupo · factura independiente"} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1F1F25] border-[#2E2E33] text-white">
                        <SelectItem value="none" data-testid="upload-group-none">Sin grupo · factura independiente</SelectItem>
                        {clientGroups.map((g) => (
                          <SelectItem key={g.id} value={g.id} data-testid={`upload-group-${g.id}`}>
                            {g.name} {g.requests?.length ? `· ${g.requests.length} solicitud${g.requests.length === 1 ? '' : 'es'}` : ''}
                          </SelectItem>
                        ))}
                        {!loadingGroups && clientGroups.length === 0 && (
                          <SelectItem value="empty" disabled>El cliente no tiene grupos SGM Web</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {uploadForm.group_id && (
                      <p className="text-[10px] font-mono text-emerald-400 mt-2 uppercase tracking-widest">
                        ✓ Se agregará al historial del grupo y se notificará al cliente por correo.
                      </p>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="overline block mb-2">Folio</label>
                    <input className="input-base" value={uploadForm.invoice_number} onChange={(e) => setUploadForm({ ...uploadForm, invoice_number: e.target.value })} data-testid="upload-folio" placeholder="A-001" />
                  </div>
                  <div>
                    <label className="overline block mb-2">Monto (MXN)</label>
                    <input type="number" step="0.01" className="input-base" value={uploadForm.amount} onChange={(e) => setUploadForm({ ...uploadForm, amount: e.target.value })} data-testid="upload-amount" />
                  </div>
                </div>
                <div>
                  <label className="overline block mb-2">Periodo</label>
                  <input type="month" className="input-base" value={uploadForm.period} onChange={(e) => setUploadForm({ ...uploadForm, period: e.target.value })} data-testid="upload-period" />
                </div>
                <div>
                  <label className="overline block mb-2">Descripción</label>
                  <input className="input-base" value={uploadForm.description} onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })} placeholder="Póliza de implementación SGM" data-testid="upload-description" />
                </div>
                <div>
                  <label className="overline block mb-2">Archivo PDF</label>
                  <input type="file" accept="application/pdf" onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })} className="input-base file:bg-[#F59E0B] file:text-black file:border-0 file:font-bold file:uppercase file:tracking-widest file:text-xs file:px-4 file:py-2 file:mr-4" data-testid="upload-file" />
                </div>
                <DialogFooter>
                  <button type="submit" disabled={uploading} className="btn-primary disabled:opacity-50 w-full" data-testid="upload-submit">
                    {uploading ? "Subiendo..." : "Subir Factura"} <Upload className="w-4 h-4" />
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* KPIs (clickable) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2E2E33] border border-[#2E2E33] mb-8 sm:mb-12">
          {[
            { label: "Clientes", value: users.length, icon: Users, tab: "clients", badge: 0 },
            { label: "Facturas", value: invoices.length, icon: FileText, tab: "invoices", badge: 0 },
            { label: "Mensajes", value: messages.length, icon: MessageSquare, tab: "messages", badge: unreadCount },
            { label: "Total Facturado", value: formatMoney(invoices.reduce((s, i) => s + (i.amount || 0), 0)), icon: Gauge, tab: "invoices", badge: 0 },
          ].map((k) => {
            const Icon = k.icon;
            const isActive = activeTab === k.tab;
            return (
              <button
                key={k.label}
                type="button"
                onClick={() => {
                  setActiveTab(k.tab);
                  setTimeout(() => document.querySelector('[data-testid="admin-tabs"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
                }}
                className={`relative text-left bg-[#1F1F25] p-4 sm:p-6 min-w-0 transition-all hover:bg-[#26262C] cursor-pointer group ${isActive ? "ring-1 ring-inset ring-[#F59E0B]" : ""}`}
                data-testid={`kpi-${k.tab}`}
              >
                <Icon className={`w-5 h-5 mb-2 sm:mb-3 transition-colors ${isActive ? "text-[#F59E0B]" : "text-[#F59E0B] group-hover:scale-110"}`} />
                <div className="font-mono text-[10px] sm:text-xs text-[#71717A] uppercase tracking-widest mb-1 sm:mb-2 truncate">{k.label}</div>
                <div className="font-heading font-black text-lg sm:text-2xl text-white truncate">{k.value}</div>
                {k.badge > 0 && (
                  <span className="absolute top-3 right-3 bg-[#EF4444] text-white font-heading font-black text-xs px-2 py-0.5 animate-pulse" data-testid={`kpi-${k.tab}-badge`}>
                    {k.badge} nuevo{k.badge > 1 ? "s" : ""}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="admin-tabs">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="bg-[#1F1F25] border border-[#2E2E33] rounded-none p-0 h-auto flex-nowrap w-max min-w-full">
              <TabsTrigger value="invoices" className="rounded-none font-heading uppercase tracking-widest text-xs px-4 sm:px-6 py-3 data-[state=active]:bg-[#F59E0B] data-[state=active]:text-black whitespace-nowrap" data-testid="tab-invoices">
                <FileText className="w-3.5 h-3.5 mr-1.5" /> Facturas
              </TabsTrigger>
              <TabsTrigger value="clients" className="rounded-none font-heading uppercase tracking-widest text-xs px-4 sm:px-6 py-3 data-[state=active]:bg-[#F59E0B] data-[state=active]:text-black whitespace-nowrap" data-testid="tab-clients">
                <Users className="w-3.5 h-3.5 mr-1.5" /> Clientes
              </TabsTrigger>
              <TabsTrigger value="messages" className="rounded-none font-heading uppercase tracking-widest text-xs px-4 sm:px-6 py-3 data-[state=active]:bg-[#F59E0B] data-[state=active]:text-black whitespace-nowrap relative" data-testid="tab-messages">
                <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Mensajes
                {unreadCount > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-[#EF4444] text-white text-[10px] font-bold rounded-none animate-pulse" data-testid="tab-messages-badge">
                    {unreadCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="quotes" className="rounded-none font-heading uppercase tracking-widest text-xs px-4 sm:px-6 py-3 data-[state=active]:bg-[#F59E0B] data-[state=active]:text-black whitespace-nowrap" data-testid="tab-quotes">
                <FileSignature className="w-3.5 h-3.5 mr-1.5" /> Cotizaciones
              </TabsTrigger>
              <TabsTrigger value="products" className="rounded-none font-heading uppercase tracking-widest text-xs px-4 sm:px-6 py-3 data-[state=active]:bg-[#F59E0B] data-[state=active]:text-black whitespace-nowrap" data-testid="tab-products">
                <Package className="w-3.5 h-3.5 mr-1.5" /> Productos
              </TabsTrigger>
              <TabsTrigger value="sgmweb" className="rounded-none font-heading uppercase tracking-widest text-xs px-4 sm:px-6 py-3 data-[state=active]:bg-[#F59E0B] data-[state=active]:text-black whitespace-nowrap" data-testid="tab-sgmweb">
                <Hash className="w-3.5 h-3.5 mr-1.5" /> SGM Web
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="invoices" className="mt-6">
            <InvoicesTab
              invoices={invoices}
              loading={loading}
              onDownload={handleDownload}
              onDelete={handleDelete}
            />
          </TabsContent>

          <TabsContent value="clients" className="mt-6">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <h3 className="font-heading font-bold uppercase text-xl tracking-tight">
                <Users className="inline w-5 h-5 text-[#F59E0B] mr-2 -mt-1" />
                Clientes Registrados
              </h3>
              <button onClick={openCreateClient} className="btn-primary py-2.5 px-4" data-testid="client-create-btn">
                <UserPlus className="w-4 h-4" /> Nuevo Cliente
              </button>
            </div>
            <div className="border border-[#2E2E33] bg-[#1F1F25]" data-testid="admin-clients-list">
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#2E2E33] font-mono text-[10px] uppercase tracking-widest text-[#71717A]">
                <div className="col-span-2">Nombre</div>
                <div className="col-span-3">Email</div>
                <div className="col-span-3">Empresa</div>
                <div className="col-span-2">Teléfono</div>
                <div className="col-span-1">Alta</div>
                <div className="col-span-1 text-right">Acciones</div>
              </div>
              {users.length === 0 ? (
                <div className="p-16 text-center">
                  <Users className="w-12 h-12 text-[#2E2E33] mx-auto mb-4" />
                  <p className="text-[#A1A1AA]">Aún no hay clientes registrados.</p>
                </div>
              ) : (
                <>
                  {clientsPager.pageItems.map((u) => (
                  <div key={u.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 px-4 sm:px-6 py-4 border-b border-[#2E2E33] last:border-b-0 hover:bg-[#26262C] items-center" data-testid={`client-row-${u.id}`}>
                    <div className="md:col-span-2 text-sm text-white font-semibold">{u.name}</div>
                    <div className="md:col-span-3 text-sm text-[#A1A1AA] font-mono break-all">{u.email}</div>
                    <div className="md:col-span-3 text-sm text-[#D4D4D8]">{u.company || "—"}</div>
                    <div className="md:col-span-2 text-sm text-[#A1A1AA] font-mono">{u.phone || "—"}</div>
                    <div className="md:col-span-1 text-xs text-[#71717A] font-mono">{u.created_at ? new Date(u.created_at).toLocaleDateString("es-MX") : "—"}</div>
                    <div className="md:col-span-1 flex md:justify-end gap-2">
                      <button onClick={() => openEditClient(u)} title="Editar" className="border border-[#2E2E33] hover:border-[#F59E0B] hover:text-[#F59E0B] p-2" data-testid={`client-edit-${u.id}`}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => deleteClient(u)} title="Eliminar" className="border border-[#2E2E33] hover:border-[#EF4444] hover:text-[#EF4444] p-2" data-testid={`client-delete-${u.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
                {clientsPager.controls}
                </>
              )}
            </div>
          </TabsContent>

          <TabsContent value="messages" className="mt-6">
            <MessagesTab
              messages={messages}
              onReply={openReply}
              onQuoteFromMessage={(m) => {
                setActiveTab("quotes");
                setTimeout(() => {
                  window.dispatchEvent(new CustomEvent("sgm:open-quote", { detail: {
                    recipient_email: m.email,
                    recipient_name: m.name || "",
                    recipient_company: m.company || "",
                    recipient_phone: m.phone || "",
                    message_id: m.id,
                    service: m.service || "",
                  }}));
                }, 80);
              }}
            />
          </TabsContent>

          <TabsContent value="quotes" className="mt-6">
            <QuotesTab />
          </TabsContent>

          <TabsContent value="products" className="mt-6">
            <ProductsTab />
          </TabsContent>

          <TabsContent value="sgmweb" className="mt-6">
            <AdminSGMTab />
          </TabsContent>
        </Tabs>
      </main>

      {/* New Message Notification Dialog */}
      <Dialog open={!!newMessageDialog} onOpenChange={(o) => { if (!o) { if (newMessageDialog) markSeen(newMessageDialog.id); setNewMessageDialog(null); } }}>
        <DialogContent className="bg-[#1F1F25] border-[#2E2E33] text-white max-w-lg rounded-none p-0 flex flex-col">
          <DialogHeader className="px-6 py-5 border-b-2 border-[#F59E0B] bg-[#141418]">
            <div className="flex items-center gap-3 mb-1">
              <span className="inline-flex items-center justify-center w-8 h-8 bg-[#EF4444] animate-pulse">
                <MessageSquare className="w-4 h-4 text-white" />
              </span>
              <DialogTitle className="font-heading font-black uppercase tracking-tight text-lg">
                Nuevo mensaje recibido
              </DialogTitle>
            </div>
            <DialogDescription className="text-[#A1A1AA] font-mono text-xs uppercase tracking-widest">
              // Contacto pendiente de respuesta
            </DialogDescription>
          </DialogHeader>
          {newMessageDialog && (
            <div className="px-6 py-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#71717A] mb-1">// Nombre</div>
                  <div className="font-heading font-bold text-white">{newMessageDialog.name}</div>
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#71717A] mb-1">// Email</div>
                  <div className="font-mono text-sm text-[#F59E0B] break-all">{newMessageDialog.email}</div>
                </div>
                {newMessageDialog.company && (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#71717A] mb-1">// Empresa</div>
                    <div className="text-white">{newMessageDialog.company}</div>
                  </div>
                )}
                {newMessageDialog.phone && (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#71717A] mb-1">// Teléfono</div>
                    <div className="font-mono text-white">{newMessageDialog.phone}</div>
                  </div>
                )}
              </div>
              {newMessageDialog.service && (
                <div className="text-[#F59E0B] text-xs font-mono uppercase tracking-widest border-l-2 border-[#F59E0B] pl-3">// Servicio: {newMessageDialog.service}</div>
              )}
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#71717A] mb-2">// Mensaje</div>
                <p className="text-[#D4D4D8] text-sm leading-relaxed whitespace-pre-wrap border border-[#2E2E33] bg-[#1A1A1F] p-4">{newMessageDialog.message}</p>
              </div>
            </div>
          )}
          <DialogFooter className="px-6 py-4 border-t border-[#2E2E33] bg-[#141418] gap-2">
            <button type="button" className="btn-ghost" onClick={() => { if (newMessageDialog) markSeen(newMessageDialog.id); setNewMessageDialog(null); }} data-testid="new-msg-dismiss">
              <X className="w-4 h-4" /> Cerrar
            </button>
            <button type="button" className="btn-ghost" onClick={() => {
              const m = newMessageDialog;
              if (m) markSeen(m.id);
              setNewMessageDialog(null);
              if (m) openReply(m);
            }} data-testid="new-msg-reply">
              <Reply className="w-4 h-4" /> Responder
            </button>
            <button type="button" className="btn-primary" onClick={() => {
              const m = newMessageDialog;
              if (m) markSeen(m.id);
              setNewMessageDialog(null);
              setActiveTab("messages");
              setTimeout(() => document.querySelector('[data-testid="admin-tabs"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
            }} data-testid="new-msg-go">
              Ver Todos <Send className="w-4 h-4" />
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Create/Edit Dialog */}
      <Dialog open={clientOpen} onOpenChange={setClientOpen}>
        <DialogContent className="bg-[#1F1F25] border-[#2E2E33] text-white max-w-xl rounded-none p-0 flex flex-col max-h-[92vh]">
          <DialogHeader className="px-6 py-5 border-b border-[#2E2E33]">
            <DialogTitle className="font-heading font-black uppercase tracking-tight">
              {clientForm.id ? "Editar cliente" : "Nuevo cliente"}
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              {clientForm.id ? "Modifica los datos. Deja el campo de contraseña vacío para no cambiarla." : "Crea una cuenta de cliente directamente desde admin."}
            </DialogDescription>
          </DialogHeader>
          <form id="client-form" onSubmit={saveClient} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">Nombre *</label>
                <input required className="input-base" value={clientForm.name}
                  onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                  data-testid="client-form-name" />
              </div>
              <div>
                <label className="overline block mb-2">Empresa</label>
                <input className="input-base" value={clientForm.company}
                  onChange={(e) => setClientForm({ ...clientForm, company: e.target.value })}
                  data-testid="client-form-company" />
              </div>
              <div>
                <label className="overline block mb-2">Email *</label>
                <input required type="email" className="input-base" value={clientForm.email}
                  onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                  data-testid="client-form-email" />
              </div>
              <div>
                <label className="overline block mb-2">Teléfono</label>
                <input className="input-base" value={clientForm.phone}
                  onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                  data-testid="client-form-phone" />
              </div>
              <div className="sm:col-span-2">
                <label className="overline block mb-2">
                  {clientForm.id ? "Nueva contraseña (opcional)" : "Contraseña *"}
                </label>
                {clientForm.id && !user?.is_superadmin ? (
                  <div className="input-base bg-[var(--bg-elevated)] text-[var(--text-muted)] flex items-center justify-between" data-testid="client-form-password-locked">
                    <span className="text-xs">🔒 Solo el super administrador puede modificar contraseñas</span>
                  </div>
                ) : (
                  <input
                    type="password"
                    minLength={6}
                    required={!clientForm.id}
                    className="input-base"
                    value={clientForm.password}
                    placeholder={clientForm.id ? "Dejar vacío para mantener la actual" : "Mínimo 6 caracteres"}
                    onChange={(e) => setClientForm({ ...clientForm, password: e.target.value })}
                    data-testid="client-form-password" />
                )}
              </div>
            </div>
          </form>
          <DialogFooter className="px-6 py-4 border-t border-[#2E2E33] bg-[#141418] gap-2">
            <button type="button" className="btn-ghost" onClick={() => setClientOpen(false)} data-testid="client-cancel">
              <X className="w-4 h-4" /> Cancelar
            </button>
            <button type="submit" form="client-form" disabled={clientSaving} className="btn-primary disabled:opacity-50" data-testid="client-save">
              {clientSaving ? "Guardando..." : (clientForm.id ? "Guardar Cambios" : "Crear Cliente")} <Save className="w-4 h-4" />
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reply Dialog */}
      <Dialog open={replyOpen} onOpenChange={setReplyOpen}>
        <DialogContent className="bg-[#1F1F25] border-[#2E2E33] text-white max-w-2xl rounded-none p-0 flex flex-col max-h-[92vh]">
          <DialogHeader className="px-6 py-5 border-b border-[#2E2E33]">
            <DialogTitle className="font-heading font-black uppercase tracking-tight">Responder mensaje</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              {replyTarget && (<>Para: <span className="text-white font-mono">{replyTarget.email}</span> · {replyTarget.name}</>)}
            </DialogDescription>
          </DialogHeader>
          <form id="reply-form" onSubmit={sendReply} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            {replyTarget && (
              <div className="border border-[#2E2E33] bg-[#1A1A1F] p-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#71717A] mb-2">// Mensaje original</div>
                <p className="text-[#A1A1AA] text-sm whitespace-pre-wrap italic">&ldquo;{replyTarget.message}&rdquo;</p>
              </div>
            )}
            <div>
              <label className="overline block mb-2">Asunto</label>
              <input className="input-base" value={replyForm.subject}
                onChange={(e) => setReplyForm({ ...replyForm, subject: e.target.value })}
                data-testid="reply-subject" />
            </div>
            <div>
              <label className="overline block mb-2">Mensaje</label>
              <textarea required rows={9} className="input-base resize-none" value={replyForm.body}
                onChange={(e) => setReplyForm({ ...replyForm, body: e.target.value })}
                data-testid="reply-body" />
              <p className="text-[#71717A] text-xs mt-2 font-mono">Se enviará desde {`<contacto@sgmmexico.com>`}</p>
            </div>
          </form>
          <DialogFooter className="px-6 py-4 border-t border-[#2E2E33] bg-[#141418] gap-2">
            <button type="button" className="btn-ghost" onClick={() => setReplyOpen(false)} data-testid="reply-cancel">
              <X className="w-4 h-4" /> Cancelar
            </button>
            <button type="submit" form="reply-form" disabled={sendingReply} className="btn-primary disabled:opacity-50" data-testid="reply-send">
              {sendingReply ? "Enviando..." : "Enviar Respuesta"} <Send className="w-4 h-4" />
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        onNavigate={handlePaletteNavigate}
      />
    </div>
  );
}
